<template>

  <v-autocomplete
    :label="label"
    :items="locationProductOptions"
    v-model.number="model"
    :clearable="clearable"
    :no-data-text="$t('alerts.no_matching_products')"
  >

    <template v-slot:append>

      <filter-button
        icon="$vertical"
        v-model="formState.vertical"
        :items="verticalOptions"
        :hint="$t('titles.filter_by_vertical')"
      />

      <filter-button
        icon="$product"
        v-model="formState.productType"
        :items="ProductTypeRegistry.getOptions()"
        :hint="$t('titles.filter_by_product_type')"
        class="ml-3"
      />

    </template>

  </v-autocomplete>

</template>

<script setup lang="ts">

import { onBeforeMount, reactive, watch } from 'vue'

// components
import FilterButton from './FilterButton.vue'

// models
import LocationProduct from '../models/LocationProduct'
import { computed } from 'vue'

// misc
import PermissionsService from '../services/PermissionsService'
import PreferencesService from '../services/PreferencesService'
import ProductTypeRegistry from '../product_types/ProductTypeRegistry'
import VerticalRegistry from '../verticals/VerticalRegistry'
import type { VAutocompleteItems } from '../types/vuetify'

/**
 * Props
 */

interface Props {
  locationId: number
  label?: string
  clearable?: boolean
  verticals?: string[]
  verticalFilter?: null | string
  showIds?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Product', // TODO: i18n
  clearable: false,
  verticals: () => [],
  verticalFilter: null,
  showIds: true,
})

const model = defineModel<null | number>({default: null})

/**
 * Initialization
 */

onBeforeMount(() => {
  if (props.verticalFilter) {
    formState.vertical = props.verticalFilter
  }
})

/**
 * Form state
 */

interface FormState {
  vertical: null | string
  productType: null | string
}

const formState = reactive<FormState>({
  vertical: null,
  productType: null,
})

watch(formState, () => {
  // if the selected LP is no longer a valid option (because the filters have been changed), clear the model
  if (model.value && !locationProductOptions.value.some(o => o.value === model.value)) {
    model.value = null
  }
})

/**
 *
 */

const locationProducts = computed<LocationProduct[]>(() => {
  return LocationProduct.query()
    .with(['product'])
    .where('locationID', props.locationId)
    .whereHas('product', (query) => {
      query.where('verticalType', (vertical: string) => {
        return props.verticals.length === 0 || props.verticals.includes(vertical)
      })
    })
    .get()
})

const locationProductOptions = computed<VAutocompleteItems>(() => {
  return locationProducts.value
    .filter(lp => {
      if (formState.productType && lp.product.productType !== formState.productType) {
        return false
      }
      if (formState.vertical && lp.product.verticalType !== formState.vertical) {
        return false
      }
      return true
    })
    .sort((lp1, lp2) => {
      // We can't use orderBy() to do this sorting in the query, because Vuex
      // passes the LP to that function without the product relation.
      const verticals = ['airport', 'monthly', 'transient', 'event', 'on_demand']
      const lp1Order = lp1.product ? verticals.indexOf(lp1.product.verticalType) : 0
      const lp2Order = lp2.product ? verticals.indexOf(lp2.product.verticalType) : 0
      if (lp1Order > lp2Order) {
        return 1
      }
      if (lp1Order < lp2Order) {
        return -1
      }
      return 0
    })
    .map((locationProduct) => {
      return {
        title: (locationProduct.product ? locationProduct.product.name : '(unknown)') + (showIds.value ? ` (${locationProduct.ID})` : ''),
        value: locationProduct.ID,
      }
    })

})

const verticalOptions = computed(() => {
  return VerticalRegistry.getOptions().filter(o => props.verticals.length === 0 || props.verticals.includes(o.value))
})

const showIds = computed<boolean>(() => {
  return PreferencesService.preferences.showIds && PermissionsService.can('seeids')
})

</script>
